import { Group, Loader, Text, TextInput } from '@mantine/core';
import { ORBIT_GRAY } from '@/theme/colors';
import { BmiDisplay, EnteredMeasurements } from './bodyMeasurements';

interface WeightHeightInputsProps {
  values: EnteredMeasurements;
  onChange: (field: keyof EnteredMeasurements, value: string) => void;
  /** Leaving a box: the card writes the weight, or the height, if it changed. */
  onCommit: (measurement: 'weight' | 'height') => void;
  /** The BMI of what the boxes hold right now, or undefined when they hold nothing usable. */
  bmi: BmiDisplay | undefined;
  saving: boolean;
  disabled?: boolean;
}

/**
 * Weight (lb) and height (ft, in) on the PN card v2.0 (BAR-2999), prefilled
 * from the latest Observations. The BMI beside them follows every keystroke,
 * truncated as the card shows every BMI (BAR-2991); the card decides on the
 * exact value.
 */
export function WeightHeightInputs({ values, onChange, onCommit, bmi, saving, disabled }: WeightHeightInputsProps) {
  const box = (field: keyof EnteredMeasurements, unit: string, width: number, measurement: 'weight' | 'height') => (
    <Group gap={4} wrap="nowrap">
      <TextInput
        size="xs"
        w={width}
        inputMode="decimal"
        aria-label={unit}
        data-testid={`pn-measurement-${field}`}
        data-measurement={measurement}
        value={values[field]}
        disabled={disabled}
        onChange={(event) => onChange(field, event.currentTarget.value)}
        onBlur={(event) => {
          // Height is one value across two boxes: written once focus leaves the
          // pair, never from feet alone on the way to inches.
          const next = event.relatedTarget as HTMLElement | null;
          if (next?.dataset.measurement === measurement) return;
          onCommit(measurement);
        }}
      />
      <Text size="xs" c={ORBIT_GRAY}>
        {unit}
      </Text>
    </Group>
  );

  return (
    <Group gap={8} wrap="wrap" align="center">
      {box('lb', 'lb', 64, 'weight')}
      {box('ft', 'ft', 44, 'height')}
      {box('inches', 'in', 44, 'height')}
      <Text size="sm" data-testid="pn-live-bmi">
        BMI{' '}
        <Text component="span" size="sm" fw={600}>
          {bmi?.value ?? '–'}
        </Text>
      </Text>
      {saving && <Loader size={12} />}
    </Group>
  );
}
